import { SlideshowSelectEvent, ThemeEvents } from '@theme/events';

const setupKey = Symbol('critic-reviews-height');

const INSTANT_CLASS = 'critic-reviews__slides--instant';

/**
 * The slideshow `aria-hidden` attributes are not always in sync the moment
 * `slideshow:select` fires (other slides are cleared in a scheduled
 * `#updateVisibleSlides` pass). Do not use `querySelector` on aria-hidden for
 * the active slide — use `event.detail.slide` or `slides[current]`.
 * @param {Element} slideshow
 * @param {Element | null | undefined} fromEvent
 * @returns {Element | null}
 */
function getActiveSlide(slideshow, fromEvent) {
  if (fromEvent instanceof Element) return fromEvent;
  if ('slides' in slideshow && 'current' in slideshow) {
    const s = /** @type {{ slides?: Element[]; current: number }} */ (slideshow);
    return s.slides?.[s.current] ?? null;
  }
  return null;
}

/**
 * @param {Element} active
 */
function measureActiveSlideHeight(active) {
  return Math.max(
    active.getBoundingClientRect().height,
    active.scrollHeight,
    active.offsetHeight
  );
}

/**
 * @param {Element} scroller
 * @param {string} px
 * @param {boolean} instant
 */
function setScrollerHeight(scroller, px, instant) {
  if (instant) {
    scroller.classList.add(INSTANT_CLASS);
    scroller.style.height = px;
    requestAnimationFrame(() => {
      scroller.classList.remove(INSTANT_CLASS);
    });
  } else {
    scroller.style.height = px;
  }
}

/**
 * @param {Element} slideshow
 * @param {Element | null} eventSlide
 * @param {object} [opt]
 * @param {boolean} [opt.animate] When true, allow CSS height transition; when false, jump (resize, first paint).
 */
function syncHeight(slideshow, eventSlide, { animate = false } = {}) {
  const scroller = slideshow.querySelector('slideshow-slides');
  if (!scroller) return;
  const active = getActiveSlide(slideshow, eventSlide);
  if (!active) return;
  const h = measureActiveSlideHeight(active);
  if (h < 1) return;
  const px = `${Math.ceil(h)}px`;
  setScrollerHeight(scroller, px, !animate);
}

/**
 * @param {Element} slideshow
 */
function setupSlideshow(slideshow) {
  if (slideshow.classList.contains('critic-reviews--single')) return;
  if (slideshow[setupKey]) return;

  const state = { hasSyncedOnce: false, ro: null, contentMo: null };
  slideshow[setupKey] = state;

  const rebindResizeObserver = () => {
    state.ro?.disconnect();
    const active = getActiveSlide(slideshow);
    if (!active) return;
    state.ro = new ResizeObserver(() => {
      syncHeight(slideshow, getActiveSlide(slideshow), { animate: false });
    });
    state.ro.observe(active);
  };

  const runSync = (eventSlide) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const fromSelectDetail = eventSlide instanceof Element;
        // Animate only after the first height lock (avoids 300ms on first paint) and
        // only when `detail.slide` is present (aria-hidden query is unreliable).
        const animate = fromSelectDetail && state.hasSyncedOnce;
        state.hasSyncedOnce = true;
        syncHeight(slideshow, eventSlide, { animate });
        rebindResizeObserver();
      });
    });
  };

  // Combined-listings variant swap (and any other morph that replaces slides
  // without replacing the slideshow element) leaves the scroller height locked
  // at the previous active slide's height. Re-measure against the first slide
  // of the new content — the slideshow's internal `current` index can point at
  // a slot that no longer exists post-swap.
  const scroller = slideshow.querySelector('slideshow-slides');
  if (scroller) {
    state.contentMo = new MutationObserver(() => {
      const firstSlide = scroller.querySelector('slideshow-slide');
      runSync(firstSlide instanceof Element ? firstSlide : null);
    });
    state.contentMo.observe(scroller, { childList: true });
  }

  const onSelect = (e) => {
    const d = 'detail' in e && e.detail && typeof e.detail === 'object' ? e.detail : null;
    const eventSlide = d && 'slide' in d && d.slide instanceof Element ? d.slide : null;
    runSync(eventSlide);
  };

  slideshow.addEventListener(SlideshowSelectEvent.eventName, onSelect);
  runSync(null);
}

export function init() {
  document.querySelectorAll('slideshow-component.critic-reviews__slideshow').forEach(setupSlideshow);
}

/**
 * Resets every critic-reviews slideshow to slide 0 and re-measures its
 * height. Used after Horizon morphs the slideshow on variant change — the
 * slideshow element identity is preserved (so our event listeners stay
 * attached) but the slides inside have been replaced; the scroll position
 * and the previously-active dot are stale, and the scroller's pixel-height
 * style still reflects the previous active slide.
 */
function resyncAll() {
  for (const slideshow of document.querySelectorAll('slideshow-component.critic-reviews__slideshow')) {
    if (!slideshow[setupKey]) {
      setupSlideshow(slideshow);
      continue;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const sl = /** @type {{ select?: (i: number, e: Event | undefined, o: { animate: boolean }) => unknown }} */ (slideshow);
        sl.select?.(0, undefined, { animate: false });
        const scroller = slideshow.querySelector('slideshow-slides');
        const firstSlide = scroller?.querySelector('slideshow-slide') ?? null;
        syncHeight(slideshow, firstSlide instanceof Element ? firstSlide : null, { animate: false });
      });
    });
  }
}

if (!window.__criticReviewsHeightsBound) {
  window.__criticReviewsHeightsBound = true;
  document.addEventListener('shopify:section:load', init);
  document.addEventListener(ThemeEvents.variantUpdate, resyncAll);
  window.addEventListener(
    'resize',
    () => {
      for (const slideshow of document.querySelectorAll('slideshow-component.critic-reviews__slideshow')) {
        syncHeight(slideshow, getActiveSlide(slideshow), { animate: false });
      }
    },
    { passive: true }
  );
}

init();
